import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, Skeleton } from "@mui/material";
import { useGetUserPerformanceQuery } from "state/api";
import {
  PageContainer,
  DataTable,
  formatCurrency,
} from "components/shared";

const Performance = () => {
  const { data, isFetching } = useGetUserPerformanceQuery();

  // Endpoint: analytic_routes/finance → { success, data: {...} }
  const finance = useMemo(() => data?.data ?? data ?? {}, [data]);

  const stats = useMemo(
    () => [
      { label: "Total Revenue", value: formatCurrency(finance.totalRevenue) },
      { label: "Paid Revenue", value: formatCurrency(finance.paidRevenue) },
      {
        label: "Pending Payments",
        value: formatCurrency(finance.pendingPayments),
      },
      { label: "Total Cost", value: formatCurrency(finance.totalCost) },
      { label: "Gross Profit", value: formatCurrency(finance.grossProfit) },
      {
        label: "Profit Margin",
        value: `${Number(finance.profitMargin || 0).toFixed(1)}%`,
      },
      { label: "Total Tax", value: formatCurrency(finance.totalTax) },
      { label: "Orders", value: finance.orderCount ?? 0 },
    ],
    [finance]
  );

  const monthlyRows = finance.revenueByMonth || [];

  const columns = useMemo(
    () => [
      {
        field: "label",
        headerName: "Month",
        render: (m) => m.label || m.month || "—",
      },
      {
        field: "revenue",
        headerName: "Revenue",
        align: "right",
        render: (m) => formatCurrency(m.revenue),
      },
    ],
    []
  );

  return (
    <PageContainer
      title="Performance"
      subtitle="Track your financial performance here"
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(3, 1fr)",
            md: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        {stats.map((s) => (
          <Card key={s.label} variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {s.label}
              </Typography>
              {isFetching ? (
                <Skeleton variant="text" width="70%" height={32} />
              ) : (
                <Typography variant="h5" fontWeight={600}>
                  {s.value}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
        Monthly Revenue
      </Typography>
      <DataTable
        columns={columns}
        rows={monthlyRows}
        loading={isFetching}
        emptyText="No revenue data available"
      />
    </PageContainer>
  );
};

export default Performance;
